import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-college-application',
  templateUrl: './college-application.component.html',
  styleUrls: ['./college-application.component.scss']
})
export class CollegeApplicationComponent implements OnInit {

  collegeId: string = '';
  activeTab: string = 'about';

  college: any = null;

  // courses
  courseListDisplay: any[] = [];

  // amenities
  amenities: string[] = []; // amenityId -> label

  // approvals
  approvalsList: any[] = [];                     // all approvals master
  selectedApprovalNames: string[] = [];          // names for this college
  hasAddress = false;

  country = '';
  state = '';
  city = '';

  address = {
    address: '',
    pincode: ''
  };
  // admissions / placements / scholarship (HTML)
  admissionsHtml: string = '';
  placementsHtml: string = '';
  scholarshipHtml: string = '';
  rankingRecord: any = null;
  rankingsList: any[] = [];      // master ranking list
  loadingRanking = false;
  constructor(
    private http: HttpClient,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // get collegeId from route: /college-details/view-college-application/:id
    const idFromRoute = this.route.snapshot.paramMap.get('id');
    this.collegeId = idFromRoute || '';

    if (!this.collegeId) {
      console.error('No collegeId in route for view page.');
      return;
    }

    // load everything
    this.loadCollege();
    this.loadCourses();
    this.loadAmenitiesForCollege();
    this.loadApprovalsMaster();
    this.loadRankingsMaster();
    this.loadRankingForCollege();
  }

  // navbar tab change
  onTabChange(tabId: string) {
    this.activeTab = tabId;
    if (tabId === 'location') {
      this.loadCollegeLocation();
    }
  }

  // common headers
  private get userHeaders() {
    return {
      Userinfo: JSON.stringify({
        _id: localStorage.getItem('adminSess'),
        role: localStorage.getItem('userRole')
      })
    };
  }

  // ----------- MAIN COLLEGE LOAD (list, then find by id) -----------
  decodeHtml(encoded: string): string {
    if (!encoded) return '';
    const txt = document.createElement('textarea');
    txt.innerHTML = encoded;
    return txt.value;
  }
  loadCollege(): void {
    this.http.get<any>(environment.apiUrl + '/college', { headers: this.userHeaders })
      .subscribe({
        next: res => {
          const list = res && res.results && Array.isArray(res.results)
            ? res.results
            : Array.isArray(res) ? res : [];

          const college = list.find((c: any) => c && c._id === this.collegeId);

          if (!college) {
            console.warn('College not found for id:', this.collegeId);
            return;
          }

          this.college = college;

          // admissions / placements / scholarship from collegeinformationsDetails (if exists)
          const info = college.collegeinformationsDetails?.[0] || null;

          this.admissionsHtml = info?.admissions
            ? this.decodeHtml(info.admissions)
            : '';

          this.placementsHtml = info?.placements
            ? this.decodeHtml(info.placements)
            : '';

          this.scholarshipHtml = info?.scholarship
            ? this.decodeHtml(info.scholarship)
            : '';

          // approvals names (if master already loaded)
          this.updateSelectedApprovalNames();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to load college list', err);
        }
      });
  }

  loadCollegeLocation(): void {
    if (!this.collegeId) {
      this.hasAddress = false;
      return;
    }

    const url = `${environment.apiUrl}/collegeaddress`;

    this.http.get<any>(url).subscribe({
      next: res => {
        const list = Array.isArray(res?.results) ? res.results : [];

        // filter addresses for this college
        const addresses = list.filter(
          (item: any) => item.college === this.collegeId
        );

        if (!addresses.length) {
          this.hasAddress = false;
          return;
        }

        // pick latest address
        const addr = addresses.sort(
          (a: any, b: any) =>
            new Date(b.updatedAt || b.createdAt).getTime() -
            new Date(a.updatedAt || a.createdAt).getTime()
        )[0];

        this.hasAddress = true;

        // map fields for HTML
        this.country = addr.countryDetails?.name || 'N/A';
        this.state = addr.stateDetails?.name || 'N/A';
        this.city = addr.cityDetails?.name || 'N/A';

        this.address = {
          address: addr.address || '',
          pincode: addr.pincode || ''
        };
      },
      error: err => {
        console.error('Failed to load college location', err);
        this.hasAddress = false;
      }
    });
  }
  // ----------- COURSES (readonly table) -----------

  loadCourses(): void {
    const url = environment.apiUrl + '/collegecoursedetails';

    this.http.get<any>(url).subscribe({
      next: (res: any) => {
        let allRows: any[] = [];

        if (res && Array.isArray(res.results)) {
          allRows = res.results.slice();
        } else if (Array.isArray(res)) {
          allRows = res.slice();
        } else if (res && Array.isArray(res.data)) {
          allRows = res.data.slice();
        } else if (res && (res.results || res.rows || res.data)) {
          const tmp = res.results || res.rows || res.data;
          allRows = Array.isArray(tmp) ? tmp.slice() : [];
        }

        // filter by this collegeId
        const filtered: any[] = [];
        for (let i = 0; i < allRows.length; i++) {
          const r = allRows[i];
          let rowCollegeId = '';

          if (r.college) {
            if (typeof r.college === 'string') rowCollegeId = r.college;
            else if (r.college._id) rowCollegeId = r.college._id;
            else if (r.college.id) rowCollegeId = r.college.id;
          }

          if (rowCollegeId === this.collegeId) {
            filtered.push(r);
          }
        }

        // build display
        this.courseListDisplay = filtered.map(row => ({
          _id: row._id || row.id || null,
          streamsName: this.getNameFromRow(row, 'streams', 'stream', 'streamDetails'),
          branchName: this.getNameFromRow(row, 'branch', 'branch', 'branchDetails'),
          courseName: this.getNameFromRow(row, 'course', 'course', 'courseDetails'),
          durationName: this.getNameFromRow(row, 'courseduration', 'duration', 'coursedurationDetails'),
          fees: (row.fees !== undefined && row.fees !== null) ? row.fees : (row.fee || row.amount || '')
        }));
      },
      error: (err: any) => {
        console.error('Failed to load course details', err);
        this.courseListDisplay = [];
      }
    });
  }

  // helper from your existing code
  getNameFromRow(row: any, field: string, alt1?: string, alt2?: string): string {
    try {
      const candidates = [field, alt1, alt2];
      for (const k of candidates) {
        if (!k) continue;
        const v = row[k];
        if (!v) continue;

        if (typeof v === 'string') {
          const details = row[`${k}Details`] || row[`${k}Detail`] || row[`${k}Data`];
          if (details) {
            if (typeof details === 'object') {
              return details.name || details.duration || details.title || String(details);
            }
            return String(details);
          }
          return v;
        }

        if (typeof v === 'object') {
          return v.name || v.title || v.duration || v.label || JSON.stringify(v);
        }
      }
    } catch {
      // ignore
    }
    return '-';
  }

  // ----------- AMENITIES (view only) -----------

  loadAmenitiesForCollege(): void {
    if (!this.collegeId) {
      this.amenities = [];
      return;
    }

    // 🔹 get the amenities doc for THIS college
    const url = `${environment.apiUrl}/collegeamenity?college=${this.collegeId}`;

    this.http.get<any>(url).subscribe({
      next: (res) => {
        let record: any = null;

        // handle different possible shapes
        if (res && res.results && Array.isArray(res.results) && res.results.length) {
          record = res.results[0];
        } else if (Array.isArray(res) && res.length) {
          record = res[0];
        } else if (res && res._id) {
          record = res;
        }

        if (!record) {
          this.amenities = [];
          return;
        }

        // 1️⃣ Best case: you already have names in amenitieDetails[]
        if (Array.isArray(record.amenitieDetails) && record.amenitieDetails.length) {
          this.amenities = record.amenitieDetails
            .map((a: any) => a && a.amenity)
            .filter((x: any) => !!x);
          return;
        }

        // 2️⃣ Fallback: only ids in record.amenities -> map via /amenity master
        const ids: string[] = (record.amenities || [])
          .map((a: any) => {
            if (typeof a === 'string') { return a; }
            return a.id || a._id || '';
          })
          .filter((x: string) => !!x);

        if (!ids.length) {
          this.amenities = [];
          return;
        }

        // load master amenity list and map ids -> names
        this.http.get<any>(`${environment.apiUrl}/amenity`).subscribe({
          next: (amenityRes) => {
            let master: any[] = [];
            if (amenityRes && amenityRes.results && Array.isArray(amenityRes.results)) {
              master = amenityRes.results;
            } else if (Array.isArray(amenityRes)) {
              master = amenityRes;
            }

            const names: string[] = [];
            ids.forEach(id => {
              const m = master.find(x => (x.id || x._id) === id);
              if (m && m.amenity) {
                names.push(m.amenity);
              }
            });

            this.amenities = names;
          },
          error: err2 => {
            console.error('Error loading master amenities:', err2);
            this.amenities = [];
          }
        });
      },
      error: (err) => {
        console.error('Error loading college amenities:', err);
        this.amenities = [];
      }
    });
  }

  get selectedAmenitiesLabels(): string[] {
    return this.amenities;
  }
  // ----------- APPROVALS (view only) -----------

  loadApprovalsMaster(): void {
    this.http.get<any>(`${environment.apiUrl}/approvals`)
      .subscribe({
        next: res => {
          this.approvalsList = res && res.results ? res.results : [];
          this.updateSelectedApprovalNames();
        },
        error: err => {
          console.error('Failed to load approvals', err);
          this.approvalsList = [];
          this.selectedApprovalNames = [];
        }
      });
  }

  private updateSelectedApprovalNames(): void {
    if (!this.college || !Array.isArray(this.approvalsList)) {
      this.selectedApprovalNames = [];
      return;
    }

    const ids: string[] = Array.isArray(this.college.approvals)
      ? this.college.approvals.map((a: any) =>
        typeof a === 'string' ? a : (a.id || a._id || '')
      ).filter((x: string) => !!x)
      : [];

    this.selectedApprovalNames = this.approvalsList
      .filter(appr => {
        const id = appr.id || appr._id || '';
        return ids.indexOf(id) !== -1;
      })
      .map(appr => appr.name || appr.title || '');
  }



  loadRankingsMaster(): void {
    const headers = {
      Userinfo: JSON.stringify({
        _id: localStorage.getItem('adminSess'),
        role: localStorage.getItem('userRole')
      })
    };

    this.http.get<any>(environment.apiUrl + '/rankings', { headers })
      .subscribe(
        res => {
          if (res && res.results && Array.isArray(res.results)) {
            this.rankingsList = res.results;
          } else if (Array.isArray(res)) {
            this.rankingsList = res;
          } else {
            this.rankingsList = [];
          }
        },
        (err: HttpErrorResponse) => {
          console.error('Failed to load rankings master', err);
          this.rankingsList = [];
        }
      );
  }

  // 🔹 Load ranking of this college: GET /collegeranking?college=<id>
  loadRankingForCollege(): void {
    if (!this.collegeId) return;

    this.loadingRanking = true;

    const headers = {
      Userinfo: JSON.stringify({
        _id: localStorage.getItem('adminSess'),
        role: localStorage.getItem('userRole'),
      }),
    };

    const url =
      environment.apiUrl +
      '/collegeranking?college=' +
      encodeURIComponent(this.collegeId);

    this.http.get<any>(url, { headers }).subscribe({
      next: res => {
        this.loadingRanking = false;

        let rec: any = null;

        // { results: [ {...} ] }
        if (res && res.results && Array.isArray(res.results) && res.results.length) {
          rec = res.results[0];
        }
        // [ {...} ]
        else if (Array.isArray(res) && res.length) {
          rec = res[0];
        }
        // { _id: ... }
        else if (res && res._id) {
          rec = res;
        }

        this.rankingRecord = rec || null;
        if (this.rankingRecord) {
          console.log('Loaded ranking record for view:', this.rankingRecord);
        } else {
          console.log('No ranking record found for this college');
        }
      },
      error: err => {
        this.loadingRanking = false;
        console.error('Failed to load ranking for college', err);
        this.rankingRecord = null;
      }
    });
  }

  // 🔹 Get ranking name from master by id
  getRankingName(id: string): string {
    if (!id || !this.rankingsList || !this.rankingsList.length) {
      return '-';
    }

    // rankings may be string id or object
    let rankingId = id;

    // if object came directly
    if (typeof id === 'object' && id !== null) {
      const anyId: any = id;
      rankingId = anyId._id || anyId.id || '';
    }

    const found = this.rankingsList.find(function (x: any) {
      return (x._id === rankingId) || (x.id === rankingId);
    });

    return found ? (found.name || rankingId) : rankingId;
  }


}
